import Header from '@/components/Header'
import Footer from '@/components/Footer'
import Breadcrumb from '@/components/Breadcrumb'
import MetaTags from '@/components/MetaTags'
import SEOSchemaMarkup from '@/components/SEOSchemaMarkup'
import CuratorSection from '@/components/CuratorSection'
import { Heart, Award, Users } from 'lucide-react'
import { useScrollAnimation } from '@/hooks/use-scroll-animation'

const Proposito = () => {
  const purposeAnimation = useScrollAnimation({ threshold: 0.2 })
  const pillarsAnimation = useScrollAnimation({ threshold: 0.2 })
  const ctaAnimation = useScrollAnimation({ threshold: 0.3 })

  return (
    <>
      <MetaTags
        title="Propósito - FotoLivro Store | Nossa Missão e Valores"
        description="Conheça o propósito da FotoLivro Store. 30 anos preservando memórias com fotolivros personalizados de alta qualidade. Missão, valores e excelência."
        type="website"
        keywords="propósito fotolivro, missão fotolivro store, valores empresa fotolivro, história fotolivro, sobre nós"
      />
      <SEOSchemaMarkup type="organization" />

      <div className="min-h-screen flex flex-col">
        <Header />
        <Breadcrumb />
        <main
          className="flex-1 bg-background"
          role="main"
          aria-label="Nosso Propósito"
        >
          {/* Propósito Section */}
          <article className="py-20">
            <div
              ref={purposeAnimation.ref}
              className={`container mx-auto px-4 max-w-4xl transition-all duration-700 ${
                purposeAnimation.isVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10'
              }`}
            >
              <header>
                <h1 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-8 text-center">
                  Nosso Propósito
                </h1>
              </header>
              <div className="prose prose-lg max-w-none text-muted-foreground leading-relaxed mb-12">
                <p className="mb-4 text-lg">
                  Acreditamos que as melhores memórias são aquelas que podemos
                  tocar e reconhecemos o poder de uma história bem contada.
                </p>
                <p className="mb-4 text-lg">
                  Para nós, cada história é única. Nossa missão é entregar mais
                  do que um álbum comum; é construir uma narrativa visual com um
                  design profissional exclusivo que valorize a sua história.
                </p>
                <p className="mb-4 text-lg">
                  Cada álbum é o resultado de um processo cuidadoso e
                  intencional. Cada etapa é pensada para que o resultado final
                  seja tão especial quanto os momentos vividos. É um trabalho
                  feito por quem entende a importância de cada recordação.
                </p>
                <p className="mb-4 text-lg">
                  Nosso compromisso é com a sua história. Atuamos como guardiões
                  de memórias, criando álbuns que vão durar gerações. Feitos
                  para serem vistos, tocados e compartilhados, hoje e sempre.
                </p>
                <p className="text-lg font-medium text-foreground">
                  Confie suas memórias a quem se importa com elas.
                </p>
              </div>
            </div>
          </article>

          {/* Pilares */}
          <section className="py-16" aria-labelledby="pillars-heading">
            <div
              ref={pillarsAnimation.ref}
              className="container mx-auto px-4 max-w-5xl"
            >
              <h2
                id="pillars-heading"
                className={`font-serif text-4xl md:text-5xl font-bold text-foreground mb-12 text-center transition-all duration-700 ${
                  pillarsAnimation.isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
              >
                Pilares da FotoLivro Store
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <article
                  className={`text-center p-6 transition-all duration-700 ${
                    pillarsAnimation.isVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-10'
                  }`}
                  style={{
                    transitionDelay: pillarsAnimation.isVisible
                      ? '150ms'
                      : '0ms',
                  }}
                >
                  <div
                    className="inline-flex p-4 bg-primary/10 rounded-full mb-4"
                    aria-hidden="true"
                  >
                    <Heart className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Curadoria com Alma
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Nosso olhar humano para selecionar, entre as centenas de
                    fotos, as que realmente importam no contexto do seu álbum.
                  </p>
                </article>

                <article
                  className={`text-center p-6 transition-all duration-700 ${
                    pillarsAnimation.isVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-10'
                  }`}
                  style={{
                    transitionDelay: pillarsAnimation.isVisible
                      ? '300ms'
                      : '0ms',
                  }}
                >
                  <div
                    className="inline-flex p-4 bg-secondary/10 rounded-full mb-4"
                    aria-hidden="true"
                  >
                    <Award className="h-8 w-8 text-secondary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Design Atemporal
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Um layout exclusivo pensado para contar a sua história.
                  </p>
                </article>

                <article
                  className={`text-center p-6 transition-all duration-700 ${
                    pillarsAnimation.isVisible
                      ? 'opacity-100 translate-y-0'
                      : 'opacity-0 translate-y-10'
                  }`}
                  style={{
                    transitionDelay: pillarsAnimation.isVisible
                      ? '450ms'
                      : '0ms',
                  }}
                >
                  <div
                    className="inline-flex p-4 bg-primary/10 rounded-full mb-4"
                    aria-hidden="true"
                  >
                    <Users className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Experiência Completa
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    Cuidamos de tudo para você, da seleção das fotos à impressão
                    final.
                  </p>
                </article>
              </div>
            </div>
          </section>

          {/* Conheça a Curadora */}
          <CuratorSection />

          {/* CTA Section */}
          <section
            className="py-20 bg-gradient-to-br from-primary/5 via-secondary/5 to-background"
            aria-labelledby="cta-heading"
          >
            <div className="container mx-auto px-4">
              <div
                ref={ctaAnimation.ref}
                className={`max-w-3xl mx-auto bg-card rounded-2xl p-6 md:p-8 shadow-sm border border-border/50 transition-all duration-700 ${
                  ctaAnimation.isVisible
                    ? 'opacity-100 scale-100'
                    : 'opacity-0 scale-95'
                }`}
              >
                <div className="text-center">
                  <h2
                    id="cta-heading"
                    className="font-serif text-2xl md:text-3xl font-semibold text-foreground mb-4"
                  >
                    Pronto Para Eternizar Suas Memórias?
                  </h2>
                  <p className="text-sm md:text-base text-muted-foreground mb-6 max-w-xl mx-auto leading-relaxed">
                    Comece a criar seu fotolivro personalizado hoje mesmo e
                    transforme suas fotos em uma obra de arte que você e sua
                    família vão guardar para sempre.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href="/#produtos"
                      className="inline-flex items-center justify-center px-5 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-all duration-300 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    >
                      Ver Nossos Produtos
                    </a>
                    <a
                      href="https://wa.me/554137950568"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-[#25D366] text-white rounded-md hover:bg-[#128C7E] transition-all duration-300 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] focus:ring-offset-2"
                      aria-label="Fale Conosco via WhatsApp"
                    >
                      <svg
                        className="h-4 w-4"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        aria-hidden="true"
                      >
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                      Fale Conosco
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  )
}

export default Proposito
